import { Link } from "react-router-dom";
import { Target, TrendingUp, LineChart, Infinity as InfinityIcon, ArrowRight, Check } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTABand } from "@/components/site/CTABand";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/Primitives";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SERVICES } from "@/lib/content";

const ICONS = { Target, TrendingUp, LineChart, Infinity: InfinityIcon };

const PROCESS = [
  { q: "01 · Mandate briefing", a: "We start with a deep briefing on the role, the team, the culture and the realistic market. Because we have done the job, the briefing is a peer conversation, not a form-filling exercise." },
  { q: "02 · Market mapping & activation", a: "We map the relevant talent across Monaco and Geneva and activate networks built over decades inside the institutions you compete with — reaching passive candidates who never see a job board." },
  { q: "03 · Assessment & shortlist", a: "Every candidate is assessed for cultural fit, compliance posture and realistic portable-book transfer — not just a matching CV. You receive a tight, pre-qualified shortlist." },
  { q: "04 · Offer, close & 12-month follow-up", a: "We manage the sensitive offer and close stage, then stay engaged through onboarding and a 12-month follow-up to protect retention." },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHero
        breadcrumb={[{ label: "Services" }]}
        eyebrow="What we do"
        title="One partner across the full talent lifecycle."
        intro="From the strategic hire to the leadership that retains them, our four services connect into a single, accountable relationship — the Beyond Partnership."
      />

      <section className="pb-4 pt-4">
        <div className="container-bp">
          <RevealGroup className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || Target;
              const gold = s.premium;
              return (
                <RevealItem key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`services-card-${s.slug}`}
                    className={`group flex h-full flex-col rounded-[var(--radius-lg)] border bg-[color:var(--bp-surface)] p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-lift)] ${gold ? "border-[color:var(--bp-gold)]/45 hover:border-[color:var(--bp-gold)]/80 grain-card" : "border-[color:var(--bp-border)] hover:border-[color:var(--bp-teal)]/60"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex h-12 w-12 items-center justify-center rounded-full border ${gold ? "border-[color:var(--bp-gold)]/40 text-[color:var(--bp-gold)]" : "border-[color:var(--bp-teal)]/40 bg-[color:var(--bp-teal-soft)] text-[color:var(--bp-teal)]"}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      {gold && <span className="rounded-full border border-[color:var(--bp-gold)]/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bp-gold)]">Integrated retainer</span>}
                    </div>
                    <h3 className="mt-6 font-display text-2xl font-semibold text-[color:var(--bp-cream)]">{s.name}</h3>
                    <p className="mt-1 text-[14px] font-medium text-[color:var(--bp-teal)]">{s.tagline}</p>
                    <p className="mt-4 flex-1 text-[14px] leading-relaxed text-[color:var(--bp-text-muted)]">{s.summary}</p>
                    <span className={`mt-6 inline-flex items-center gap-2 text-[13px] font-semibold ${gold ? "text-[color:var(--bp-gold)]" : "text-[color:var(--bp-teal)]"}`}>
                      Explore {s.short} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* Process */}
      <section className="section-py" data-testid="process-section">
        <div className="container-bp grid grid-cols-1 gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading eyebrow="Our methodology" title="A 47-touchpoint retained process — built by practitioners." intro="Every engagement follows a disciplined process, from peer-level briefing to a 12-month retention follow-up." />
          <Reveal>
            <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
              {PROCESS.map((p, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-[color:var(--bp-border)]">
                  <AccordionTrigger className="text-left font-display text-lg font-semibold text-[color:var(--bp-cream)] hover:text-[color:var(--bp-teal)] hover:no-underline">{p.q}</AccordionTrigger>
                  <AccordionContent className="text-[14px] leading-relaxed text-[color:var(--bp-text-muted)]">{p.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CTABand title="Which part of the lifecycle can we solve first?" />
    </div>
  );
}
